import React, { useState, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  TableContainer,
  Tooltip,
  Autocomplete,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InputAdornment from "@mui/material/InputAdornment";
import Cookie from "js-cookie";
import { useMaterialUIController } from "context";
import {
  ViewButton,
  DeleteButton,
  StyledTypography,
  StyledButton,
  StyledButtonC,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { employeeStyles } from "components/Styles/employeeMasterStyles";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";

export default function Applicant() {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    address: {
      doorNo: "",
      streetName: "",
      cityName: "",
      districtName: "",
      state: "",
      zipCode: "",
    },
    phone: "",
    emailId: "",
    alternativePhone: "",
    reference: "",
    role: null,
    ctcPerYear: "",
    basic: "",
    dateOfJoining: "",
    resume: null,
    resumeUrl: "",
    resumeAttachmentPresignedUrl: "",
    additionalComments: "",
    skills: [],
    workExperience: {
      company: "",
      position: "",
      startDate: "",
      endDate: "",
    },
    firstRoundStatus: "N",
    secondRoundStatus: "N",
    firstRoundFeedback: "",
    secondRoundFeedback: "",
  });
  const statusOptions = ["Cleared", "Not Cleared"];
  const [roleData, setRoleData] = useState([]);
  const [hrms_token] = useState(Cookie.get("hrms_token"));
  const [loading, setLoading] = useState(false);
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [formErrors, setFormErrors] = useState({});
  const [phonePattern] = useState(/^[1-9]\d{9}$/);
  const [emailPattern] = useState(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  const [openResumeDialog, setOpenResumeDialog] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const MInput = inputStyle();

  useEffect(() => {
    fetchRoleData();
    fetchApplicants();
  }, []);

  const fetchRoleData = async () => {
    setLoading(true);
    const url = `${URLs.hrmsGetRoleDetails}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setRoleData(data);
    } catch (error) {
      console.error("Error fetching role data:", error);
      setMessage("Failed to fetch role data");
      setAlertType("error");
      setOpen(true);
    }
    setLoading(false);
  };

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      if (!user || !user.dsaId) {
        console.error("Company ID not found");
        setMessage("Company ID not found");
        setAlertType("error");
        setOpen(true);
        return;
      }

      const response = await fetch(`${URLs.hrmsGetAllApplicantsBy}/${user.dsaId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch applicants");
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error("Received data is not an array:", data);
        throw new Error("Invalid data format received");
      }

      const applicantsWithRoles = await Promise.all(
        data.map(async (applicant) => {
          if (applicant.roleId) {
            try {
              const roleResponse = await fetch(`${URLs.hrmsGetRoleDetails}/${applicant.roleId}`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${hrms_token}`,
                },
              });
              if (roleResponse.ok) {
                const roleData = await roleResponse.json();
                return {
                  ...applicant,
                  role: roleData,
                  firstRoundStatus: applicant.firstRoundStatus === "Y" ? "Cleared" : "Not Cleared",
                  secondRoundStatus:
                    applicant.secondRoundStatus === "Y" ? "Cleared" : "Not Cleared",
                };
              }
            } catch (error) {
              console.error("Error fetching role:", error);
            }
          }
          return {
            ...applicant,
            firstRoundStatus: applicant.firstRoundStatus === "Y" ? "Cleared" : "Not Cleared",
            secondRoundStatus: applicant.secondRoundStatus === "Y" ? "Cleared" : "Not Cleared",
          };
        })
      );
      console.log("Fetched applicants:", applicantsWithRoles);
      setTableData(applicantsWithRoles);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setMessage("Failed to fetch applicants");
      setAlertType("error");
      setOpen(true);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      if (parent === "workExperience") {
        setFormData((prev) => ({
          ...prev,
          workExperience: {
            ...prev.workExperience,
            [child]: value,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        }));
      }
    } else {
      if (name === "ctcPerYear") {
        const ctcValue = value.replace(/,/g, "");
        const basicValue = (parseFloat(ctcValue) * 0.42).toFixed(2);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          basic: basicValue.toString(),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
  };

  const handleRoleChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      role: newValue,
    }));
  };

  const handleSkillsChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      skills: newValue,
    }));
  };

  const handleStatusChange = (name, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue || "Not Cleared",
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // File size validation
    if (file.size > 5 * 1024 * 1024) {
      setMessage("File size should be less than 5MB");
      setAlertType("error");
      setOpen(true);
      return;
    }

    // File type validation
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      setMessage("Invalid file format. Only PDF, DOC, and DOCX are allowed.");
      setAlertType("error");
      setOpen(true);
      return;
    }

    // Validate phone number
    // if (!formData.phone || !phonePattern.test(formData.phone)) {
    //   setMessage("Please enter a valid 10-digit phone number before uploading the resume");
    //   setAlertType("error");
    //   setOpen(true);
    //   return;
    // }

    setLoading(true);

    try {
      // Use phone number as the unique identifier in the file name
      const uniqueFileName = `${formData.phone}_${file.name}`;
      // Request pre-signed S3 URL from backend
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        uniqueFileName
      )}&bucketName=sconexsofthrms&operation=upload`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      // Log response for debugging
      console.log("S3 Pre-signed URL Response:", response.data);

      // Since response.data is the URL string
      const url = response.data;

      // Validate URL
      if (!url || typeof url !== "string" || !url.startsWith("https://")) {
        throw new Error("Invalid pre-signed URL");
      }

      // Upload file to S3 using pre-signed URL
      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      // Update formData with file and S3 URL
      setFormData((prev) => ({
        ...prev,
        resume: file,
        resumeUrl: url.split("?")[0],
        resumeAttachmentPresignedUrl: url.split("?")[0],
      }));

      setMessage("Resume uploaded successfully!");
      setAlertType("success");
      setOpen(true);
    } catch (err) {
      console.error("Upload Error:", err);
      setMessage(`Failed to upload resume: ${err.message || "Unknown error"}`);
      setAlertType("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = "First Name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last Name is required";
    if (!formData.dob) errors.dob = "Date of Birth is required";
    if (!formData.role) errors.role = "Role is required";
    if (!formData.phone) errors.phone = "Phone Number is required";
    if (!formData.emailId) errors.emailId = "Email is required";
    if (!formData.dateOfJoining) errors.dateOfJoining = "Date of Joining is required";
    if (!formData.ctcPerYear || isNaN(parseFloat(formData.ctcPerYear.replace(/,/g, ""))))
      errors.ctcPerYear = "Valid CTC is required";
    if (!formData.basic || isNaN(parseFloat(formData.basic.replace(/,/g, ""))))
      errors.basic = "Valid Basic is required";

    if (!isEditMode && !formData.resumeUrl && !formData.resume)
      errors.resume = "Resume is required";

    if (formData.phone && !phonePattern.test(formData.phone)) {
      errors.phone = "Enter valid 10-digit phone number";
    }
    if (formData.alternativePhone && !phonePattern.test(formData.alternativePhone)) {
      errors.alternativePhone = "Enter valid 10-digit phone number";
    }
    if (formData.emailId && !emailPattern.test(formData.emailId)) {
      errors.emailId = "Enter valid email address";
    }
    if (formData.address.zipCode && !/^\d{6}$/.test(formData.address.zipCode)) {
      errors.zipCode = "Enter valid 6-digit zip code";
    }

    const ctc = parseFloat(formData.ctcPerYear?.replace(/,/g, "")) || 0;
    const basic = parseFloat(formData.basic?.replace(/,/g, "")) || 0;
    if (basic > ctc && ctc > 0) {
      errors.basic = "Basic cannot be greater than CTC";
    }

    console.log("Form Errors:", errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form Data:", formData);
    if (!validateForm()) {
      setMessage("Please fix the form errors");
      setAlertType("error");
      setOpen(true);
      return;
    }

    if (!formData.role || !formData.role.roleId) {
      setMessage("Please select a role");
      setAlertType("error");
      setOpen(true);
      return;
    }

    const payload = {
      applicantId: isEditMode ? formData.applicantId : 0,
      companyId: user.companyId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfJoining: formData.dateOfJoining,
      dateOfBirth: formData.dob,
      doorNo: formData.address.doorNo,
      empAddress: {
        streetName: formData.address.streetName,
        cityName: formData.address.cityName,
        districtName: formData.address.districtName,
        stateName: formData.address.state,
        zipCode: formData.address.zipCode,
      },
      phoneNumber: formData.phone,
      alternatePhoneNumber: formData.alternativePhone,
      emailId: formData.emailId,
      reference: formData.reference,
      role: {
        roleId: formData.role.roleId,
        roleName: formData.role.roleName,
      },
      ctcPerYear: parseFloat(formData.ctcPerYear?.replace(/,/g, "")) || 0,
      basic: parseFloat(formData.basic?.replace(/,/g, "")) || 0,
      additionalComments: formData.additionalComments,
      roleId: formData.role.roleId,
      skills: formData.skills,
      workExperienceDto: {
        company: formData.workExperience.company,
        position: formData.workExperience.position,
        startDate: formData.workExperience.startDate
          ? new Date(formData.workExperience.startDate).toISOString()
          : null,
        endDate: formData.workExperience.endDate
          ? new Date(formData.workExperience.endDate).toISOString()
          : null,
      },
      firstRoundStatus: formData.firstRoundStatus === "Cleared" ? "Y" : "N",
      secondRoundStatus: formData.secondRoundStatus === "Cleared" ? "N" : "N",
      firstRoundFeedback: formData.firstRoundFeedback,
      secondRoundFeedback: formData.secondRoundFeedback,
      resumeAttachmentPresignedUrl: formData.resumeUrl || "",
    };

    try {
      const url = isEditMode
        ? `${URLs.hrmsUpdateApplicant}/${formData.applicantId}`
        : URLs.hrmsPostApplicant;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Backend Error:", errorData);
        throw new Error(
          errorData.message ||
            (isEditMode ? "Failed to update applicant" : "Failed to create applicant")
        );
      }

      setMessage(isEditMode ? "Applicant updated successfully" : "Applicant created successfully");
      setAlertType("success");
      setOpen(true);
      handleCancel();
      fetchApplicants();
    } catch (error) {
      console.error("Submit Error:", error);
      setMessage(error.message);
      setAlertType("error");
      setOpen(true);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${URLs.hrmsDeleteApplicant}/${deleteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete applicant");
      }

      setMessage("Applicant deleted successfully");
      setAlertType("success");
      setOpen(true);
      setOpenDialog(false);
      fetchApplicants();
    } catch (error) {
      console.error("Error:", error);
      setMessage(error.message);
      setAlertType("error");
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [{ label: "Applicant List" }, { label: "Applicant Registration" }];

  const handleCancel = () => {
    setFormData({
      firstName: "",
      lastName: "",
      dob: "",
      address: {
        doorNo: "",
        streetName: "",
        cityName: "",
        districtName: "",
        state: "",
        zipCode: "",
      },
      phone: "",
      emailId: "",
      alternativePhone: "",
      reference: "",
      role: null,
      ctcPerYear: "",
      basic: "",
      dateOfJoining: "",
      resume: null,
      resumeUrl: "",
      resumeAttachmentPresignedUrl: "",
      additionalComments: "",
      skills: [],
      workExperience: {
        company: "",
        position: "",
        startDate: "",
        endDate: "",
      },
      firstRoundStatus: "N",
      secondRoundStatus: "N",
      firstRoundFeedback: "",
      secondRoundFeedback: "",
    });
    setIsEditMode(false);
    setActiveTab(0);
  };

  const ActionCellRenderer = (props) => {
    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex).data;

    const handleEditClick = () => {
      const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          return date instanceof Date && !isNaN(date) ? date.toISOString().split("T")[0] : "";
        } catch (e) {
          console.error("Error formatting date:", e);
          return "";
        }
      };

      const applicantData = {
        ...rowData,
        dob: formatDate(rowData.dateOfBirth),
        dateOfJoining: formatDate(rowData.dateOfJoining),
        address: {
          doorNo: rowData.doorNo || "",
          streetName: rowData.empAddress?.streetName || "",
          cityName: rowData.empAddress?.cityName || "",
          districtName: rowData.empAddress?.districtName || "",
          state: rowData.empAddress?.stateName || "",
          zipCode: rowData.empAddress?.zipCode || "",
        },
        phone: rowData.phoneNumber || "",
        alternativePhone: rowData.alternatePhoneNumber || "",
        role: rowData.role || null,
        resume: null,
        resumeUrl: rowData.resumeAttachmentPresignedUrl || "",
        resumeAttachmentPresignedUrl: rowData.resumeAttachmentPresignedUrl || "",
        ctcPerYear: rowData.ctcPerYear?.toString() || "",
        basic: rowData.basic?.toString() || "",
        skills: rowData.skills || [],
        workExperience: rowData.workExperience || {
          company: "",
          position: "",
          startDate: "",
          endDate: "",
        },
        firstRoundStatus: rowData.firstRoundStatus || "Not Cleared",
        secondRoundStatus: rowData.secondRoundStatus || "Not Cleared",
        firstRoundFeedback: rowData.firstRoundFeedback || "",
        secondRoundFeedback: rowData.secondRoundFeedback || "",
      };

      console.log("Edit data:", applicantData);
      setFormData(applicantData);
      setIsEditMode(true);
      setActiveTab(1);
    };

    const handleDelete = () => {
      setDeleteId(rowData.applicantId);
      setOpenDialog(true);
    };

    return (
      <div>
        <ViewButton type="button" onClick={handleEditClick}>
          <EditIcon />
        </ViewButton>
        <DeleteButton type="button" onClick={handleDelete}>
          <DeleteIcon />
        </DeleteButton>
      </div>
    );
  };

  const ResumeViewer = ({ url }) => {
    if (!url) return null;

    return (
      <object data={url} type="application/pdf" style={{ width: "100%", height: "80vh" }}>
        <p>
          Unable to display PDF. Please download to view: <a href={url}>Download</a>
        </p>
      </object>
    );
  };

  ResumeViewer.propTypes = {
    url: PropTypes.string,
  };

  // Helper to get file name from S3 URL
  const getFileNameFromUrl = (url) => {
    try {
      return decodeURIComponent(url.split("/").pop().split("?")[0]);
    } catch {
      return "resume";
    }
  };

  const handleResumeView = async (resumeUrl) => {
    if (!resumeUrl) {
      setMessage("No resume available to view");
      setAlertType("error");
      setOpen(true);
      return;
    }

    setLoading(true);
    try {
      const fileName = getFileNameFromUrl(resumeUrl);
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}&bucketName=sconexsofthrms&operation=download`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      const viewUrl = response.data;
      if (!viewUrl || typeof viewUrl !== "string" || !viewUrl.startsWith("https://")) {
        throw new Error("Invalid pre-signed URL for viewing");
      }

      setSelectedResume(viewUrl);
      setOpenResumeDialog(true);
      setMessage("Resume loaded successfully");
      setAlertType("success");
      setOpen(true);
    } catch (err) {
      console.error("Error fetching resume URL:", err);
      setMessage(`Failed to load resume: ${err.message || "Unknown error"}`);
      setAlertType("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseResumeDialog = () => {
    setOpenResumeDialog(false);
    setSelectedResume(null);
  };

  // Function to get pre-signed download URL and trigger download
  const handleDownloadResume = async () => {
    if (!selectedResume) return;
    setDownloading(true);
    try {
      // Use the same logic as upload, but with operation=download
      const fileName = getFileNameFromUrl(selectedResume);
      const apiUrl = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        fileName
      )}&bucketName=sconexsofthrms&operation=download`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      const downloadUrl = response.data;
      if (!downloadUrl || typeof downloadUrl !== "string") throw new Error("Invalid download URL");

      // Trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setMessage("Failed to download resume");
      setAlertType("error");
      setOpen(true);
    } finally {
      setDownloading(false);
    }
  };

  const columnDefs = [
    {
      headerName: "Applicant Name",
      field: "applicantName",
      valueGetter: (params) => {
        return `${params.data.firstName || ""} ${params.data.lastName || ""}`.trim();
      },
      sortable: true,
      filter: true,
      flex: 2,
    },
    {
      headerName: "Email Id",
      field: "emailId",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Phone Number",
      field: "phoneNumber",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Role",
      field: "role",
      valueGetter: (params) => {
        const role = params.data.role;
        return role ? role.roleName : "";
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "CTC Per Annum",
      field: "ctcPerYear",
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueGetter: (params) => {
        const ctc = params.data.ctcPerYear || 0;
        return `₹${Number(ctc).toLocaleString("en-IN")}`;
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Basic",
      field: "basic",
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueGetter: (params) => {
        const basic = params.data.basic || 0;
        return `₹${Number(basic).toLocaleString("en-IN")}`;
      },
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Resume",
      field: "resumeAttachmentPresignedUrl",
      cellRenderer: (params) => {
        if (!params.value) return null;
        return (
          <Tooltip title="View Resume">
            <IconButton
              size="small"
              onClick={() => handleResumeView(params.value)}
              sx={{ color: "#1976d2" }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        );
      },
      width: 100,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      width: 100,
    },
  ];

  const handleReset = () => {
    setFormData({
      firstName: "",
      lastName: "",
      dob: "",
      address: {
        doorNo: "",
        streetName: "",
        cityName: "",
        districtName: "",
        state: "",
        zipCode: "",
      },
      phone: "",
      emailId: "",
      alternativePhone: "",
      reference: "",
      role: null,
      ctcPerYear: "",
      basic: "",
      dateOfJoining: "",
      resume: null,
      resumeUrl: "",
      resumeAttachmentPresignedUrl: "",
      additionalComments: "",
      skills: [],
      workExperience: {
        company: "",
        position: "",
        startDate: "",
        endDate: "",
      },
      firstRoundStatus: "N",
      secondRoundStatus: "N",
      firstRoundFeedback: "",
      secondRoundFeedback: "",
    });
    setIsEditMode(false);
  };

  ActionCellRenderer.propTypes = {
    api: PropTypes.shape({
      getDisplayedRowAtIndex: PropTypes.func.isRequired,
    }).isRequired,
    node: PropTypes.shape({
      rowIndex: PropTypes.number.isRequired,
    }).isRequired,
  };

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          flexGrow: 1,
          boxShadow: "none",
          border: "none",
          height: "100%",
          backgroundColor: "transparent",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Dialog open={openDialog}>
          <DialogContent sx={subDialogContentStyle}>
            <DialogContentText sx={dialogContentTextStyle}>
              Are you sure you want to delete this applicant?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={employeeStyles.dialogActions}>
            <StyledButton onClick={handleDelete}>Yes</StyledButton>
            <StyledButton onClick={() => setOpenDialog(false)} variant="outlined">
              No
            </StyledButton>
          </DialogActions>
        </Dialog>
        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          severity={alertType}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
          sx={{
            width: "100%",
            "& .MuiTabs-flexContainer": {
              justifyContent: "flex-start",
              width: "100%",
            },
            "& .MuiTab-root": {
              minWidth: "250px",
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            mt: 1,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
            width: "100%",
            height: "calc(100% - 48px)",
            overflow: "hidden",
          }}
        >
          {activeTab === 0 && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                borderRadius: "15px",
              }}
            >
              <StyledAgGridContainer
                component={Paper}
                className={`ag-theme-alpine ${employeeStyles.agTable}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "15px",
                }}
              >
                <AgGridReact
                  rowData={tableData}
                  columnDefs={columnDefs}
                  pagination={true}
                  paginationPageSize={9}
                />
              </StyledAgGridContainer>
            </Box>
          )}
          {activeTab === 1 && (
            <StyledTableContainer
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                backgroundColor: "#ffffff",
                width: "100%",
                height: "100%",
                borderRadius: "15px",
                p: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  overflowY: "auto",
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    width: "100%",
                    m: 0,
                  }}
                >
                  <Grid item xs={12}>
                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "none",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div style={{ position: "relative", width: "100%" }}>
                        {isEditMode && (
                          <Tooltip title="Reset">
                            <StyledButtonC onClick={handleReset}>
                              <RestartAltIcon />
                            </StyledButtonC>
                          </Tooltip>
                        )}
                      </div>
                      <Grid sx={{ ...employeeStyles.root, p: 2 }}>
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <Grid container spacing={2} sx={{ p: 2 }}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="First Name"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.firstName}
                                helperText={formErrors.firstName}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.lastName}
                                helperText={formErrors.lastName}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                type="date"
                                label="Date of Birth"
                                name="dob"
                                value={formData.dob}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.dob}
                                helperText={formErrors.dob}
                                InputLabelProps={{ shrink: true }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                options={roleData}
                                getOptionLabel={(option) => option.roleName || ""}
                                value={formData.role}
                                onChange={handleRoleChange}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Role"
                                    required
                                    error={!!formErrors.role}
                                    helperText={formErrors.role}
                                    className={MInput.root}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.phone}
                                helperText={formErrors.phone}
                                inputProps={{
                                  maxLength: 10,
                                  pattern: "[1-9]{1}[0-9]{9}",
                                }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Email ID"
                                name="emailId"
                                type="email"
                                value={formData.emailId}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.emailId}
                                helperText={formErrors.emailId}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Alternative Phone"
                                name="alternativePhone"
                                value={formData.alternativePhone}
                                onChange={handleInputChange}
                                error={!!formErrors.alternativePhone}
                                helperText={formErrors.alternativePhone}
                                inputProps={{
                                  maxLength: 10,
                                  pattern: "[1-9]{1}[0-9]{9}",
                                }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Door No"
                                name="address.doorNo"
                                value={formData.address.doorNo}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Street Name"
                                name="address.streetName"
                                value={formData.address.streetName}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="City"
                                name="address.cityName"
                                value={formData.address.cityName}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                name="address.districtName"
                                fullWidth
                                label="District"
                                value={formData.address.districtName}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="State"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Zip Code"
                                name="address.zipCode"
                                value={formData.address.zipCode}
                                onChange={handleInputChange}
                                error={!!formErrors.zipCode}
                                helperText={formErrors.zipCode}
                                inputProps={{
                                  maxLength: 6,
                                  pattern: "\\d{6}",
                                }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Reference"
                                name="reference"
                                value={formData.reference}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="CTC per Year"
                                name="ctcPerYear"
                                value={formData.ctcPerYear}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.ctcPerYear}
                                helperText={formErrors.ctcPerYear}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">₹</InputAdornment>
                                  ),
                                }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Basic"
                                name="basic"
                                value={formData.basic}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.basic}
                                helperText={formErrors.basic}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">₹</InputAdornment>
                                  ),
                                }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                type="date"
                                label="Date of Joining"
                                name="dateOfJoining"
                                value={formData.dateOfJoining}
                                onChange={handleInputChange}
                                required
                                error={!!formErrors.dateOfJoining}
                                helperText={formErrors.dateOfJoining}
                                InputLabelProps={{ shrink: true }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                type="file"
                                label="Resume"
                                name="resume"
                                fullWidth
                                required
                                onChange={handleFileChange}
                                className={MInput.root}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                  accept: ".pdf,.doc,.docx",
                                }}
                                disabled={loading}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={formData.skills}
                                onChange={handleSkillsChange}
                                renderTags={(value, getTagProps) =>
                                  value.map((option, index) => (
                                    <Chip
                                      key={option || index}
                                      variant="outlined"
                                      label={option}
                                      {...getTagProps({ index })}
                                    />
                                  ))
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Skills"
                                    placeholder="Add skills"
                                    className={MInput.root}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Last Company"
                                name="workExperience.company"
                                value={formData.workExperience.company}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Last Position"
                                name="workExperience.position"
                                value={formData.workExperience.position}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                type="date"
                                label="Work Start Date"
                                name="workExperience.startDate"
                                value={formData.workExperience.startDate}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                type="date"
                                label="Work End Date"
                                name="workExperience.endDate"
                                value={formData.workExperience.endDate}
                                onChange={handleInputChange}
                                InputLabelProps={{ shrink: true }}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                options={statusOptions}
                                value={formData.firstRoundStatus}
                                onChange={(event, newValue) =>
                                  handleStatusChange("firstRoundStatus", newValue)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="First Round Status"
                                    className={MInput.root}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Autocomplete
                                options={statusOptions}
                                value={formData.secondRoundStatus}
                                onChange={(event, newValue) =>
                                  handleStatusChange("secondRoundStatus", newValue)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Second Round Status"
                                    className={MInput.root}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="First Round Feedback"
                                name="firstRoundFeedback"
                                multiline
                                rows={2}
                                value={formData.firstRoundFeedback}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Second Round Feedback"
                                name="secondRoundFeedback"
                                multiline
                                rows={2}
                                value={formData.secondRoundFeedback}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                              <TextField
                                fullWidth
                                label="Additional Comments"
                                name="additionalComments"
                                multiline
                                rows={2}
                                value={formData.additionalComments}
                                onChange={handleInputChange}
                                className={MInput.root}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              sm={4}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 2 }}>
                                {isEditMode && (
                                  <StyledButton onClick={handleCancel} variant="outlined">
                                    Cancel
                                  </StyledButton>
                                )}
                                <StyledButton type="submit" disabled={loading}>
                                  {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                  ) : isEditMode ? (
                                    "Update"
                                  ) : (
                                    "Submit"
                                  )}
                                </StyledButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </form>
                      </Grid>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Box>
            </StyledTableContainer>
          )}
        </Box>
      </Box>
      <Dialog open={openResumeDialog} onClose={handleCloseResumeDialog} maxWidth="md" fullWidth>
        <DialogContent>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ResumeViewer url={selectedResume} />
          )}
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleDownloadResume} disabled={downloading || loading}>
            {downloading ? "Downloading..." : "Download"}
          </StyledButton>
          <StyledButton onClick={handleCloseResumeDialog}>Close</StyledButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
